import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"

import { ListItemStack } from "@molecules"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"
import ListItemDivider from "@molecules/ListItem/ListItemDivider/ListItemDivider"

import { Settings } from "@templates/index"

import {
    JobsAssignmentNotificationTopics,
    JobsNotificationTopics,
} from "@pages/Settings/SettingsNotifications/SettingsNotification.types"
import {
    notificationPreferenceQueryKey,
    useNotificationPreferencesQuery,
} from "@pages/Settings/SettingsNotifications/SettingsNotifications.store"
import NotificationProperty from "@pages/Settings/SettingsNotifications/components/NotificationProperty/NotificationProperty"

import { NOTIFICATION_ENDPOINTS } from "@endpoints/notification"

export default function JobsAssignmentNotificationsForm() {
    const [shouldSetDefaultFormData, setShouldSetDefaultFormData] = useState<boolean>(false)

    const { topicPreference: jobsNotificationPreference, isLoading: isLoadingData } =
        useNotificationPreferencesQuery<JobsNotificationTopics>("jobs")

    const queryClient = useQueryClient()

    useEffect(() => {
        if (!isLoadingData && !!jobsNotificationPreference) {
            setShouldSetDefaultFormData(true)
        }
    }, [isLoadingData])

    const onMutationSuccess = () => {
        void queryClient.invalidateQueries({ queryKey: notificationPreferenceQueryKey })
    }

    const onAfterFormValuesSet = () => {
        setShouldSetDefaultFormData(false)
    }

    return (
        <GenericForm<
            JobsAssignmentNotificationTopics,
            JobsAssignmentNotificationTopics,
            Error,
            JobsAssignmentNotificationTopics
        >
            formConfig={{
                defaultValues: undefined,
            }}
            mutationConfig={{
                genericErrorMessage: "Could not update notification preference",
                genericSuccessMessage: "Notification preference updated",
                method: "PATCH",
                endpoint: NOTIFICATION_ENDPOINTS.UPDATE_NOTIFICATION_PREFERENCES,
            }}
            onMutationSuccess={onMutationSuccess}
            formValues={shouldSetDefaultFormData ? jobsNotificationPreference.assignment : undefined}
            onAfterFormValuesSet={onAfterFormValuesSet}
            isAutoSave={true}
        >
            {() => {
                return (
                    <Settings.NotificationConfigPage.Wrapper>
                        <ListItemStack>
                            <NotificationProperty<JobsAssignmentNotificationTopics>
                                label="Job assigned"
                                supportText="Notify me when I am assigned a job."
                                emailCheckboxName="enable_job_notification_job_assigned_email"
                                smsCheckboxName="enable_job_notification_job_assigned_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsAssignmentNotificationTopics>
                                label="Job unassigned"
                                supportText="Notify me when I am unassigned from a job."
                                emailCheckboxName="enable_job_notification_job_unassigned_email"
                                smsCheckboxName="enable_job_notification_job_unassigned_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsAssignmentNotificationTopics>
                                label="Job updated"
                                supportText="Notify me when someone updates a job I was assigned."
                                emailCheckboxName="enable_job_notification_job_updated_email"
                                smsCheckboxName="enable_job_notification_job_updated_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsAssignmentNotificationTopics>
                                label="Job rescheduled"
                                supportText="Notify me when someone reschedules a job I was assigned."
                                emailCheckboxName="enable_job_notification_job_rescheduled_email"
                                smsCheckboxName="enable_job_notification_job_rescheduled_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsAssignmentNotificationTopics>
                                label="Job unscheduled"
                                supportText="Notify me when someone unschedules a job I was assigned."
                                emailCheckboxName="enable_job_notification_job_unscheduled_email"
                                smsCheckboxName="enable_job_notification_job_unscheduled_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<JobsAssignmentNotificationTopics>
                                label="Job cancelled"
                                supportText="Notify me when someone cancels a job I was assigned."
                                emailCheckboxName="enable_job_notification_job_cancelled_email"
                                smsCheckboxName="enable_job_notification_job_cancelled_sms"
                                isLoading={isLoadingData}
                            />
                        </ListItemStack>
                    </Settings.NotificationConfigPage.Wrapper>
                )
            }}
        </GenericForm>
    )
}
